import type { TerrainType, OverlayType } from '~shared/types';
import { getCombinedTerrainEffect } from '~shared/terrain-rules';

const TERRAIN_NAMES: Record<TerrainType, string> = {
  clear:    'Clear ground',
  forest:   'Forest',
  hill:     'Hill',
  town:     'Town',
  river:    'River',
  bridge:   'Bridge',
  beach:    'Beach',
  hedgerow: 'Hedgerow',
  pond:          'Pond',
  ocean:         'Ocean / Sea',
  bluff:             'Bluff (cliff face)',
  'frozen-lake':     'Frozen Lake',
  'impassable-hill': 'Impassable Hill',
  ravine:            'Ravine',
  trench:            'Trench',
  'city-ruins':      'City Ruins',
  'hill-forest':     'Hill with Forest',
  'hill-village':    'Hill with Village',
  'factory-complex': 'Factory Complex',
  marsh:             'Marsh',
};

const OVERLAY_NAMES: Record<OverlayType, string> = {
  'barbed-wire': 'Barbed Wire',
  'sandbags':    'Sandbags',
  'hedgehog':    'Hedgehog',
  'bunker':      'Bunker',
};

export function getTerrainSummary(type: TerrainType, overlay?: OverlayType, permanent?: boolean): string {
  const effect = getCombinedTerrainEffect(type, overlay);
  const lines: string[] = [];

  // Title — permanent sandbags are scenario-flavoured fortifications (e.g. Omaha
  // Beach's "Sea Wall"); the underlying rules are still plain sandbags.
  const overlayName = overlay === 'sandbags' && permanent ? 'Sea Wall' : overlay ? OVERLAY_NAMES[overlay] : undefined;
  const name = overlayName ? `${overlayName} (on ${TERRAIN_NAMES[type]})` : TERRAIN_NAMES[type];
  lines.push(name);
  lines.push('');

  // Bluff directional rules (not expressible via TerrainEffect fields)
  if (type === 'bluff' && !overlay) {
    lines.push(`• From beach: infantry may enter but must stop (full move)`);
    lines.push(`✗ From beach: armor cannot enter at all`);
    lines.push(`• From inland: treated as normal hill`);
  }

  // Movement
  if (effect.impassableTo.length > 0) {
    lines.push(`✗ Impassable for ${effect.impassableTo.join(', ')}`);
  } else if (effect.stopsMovement) {
    const exempt = effect.moveExempt.length > 0 ? ` (${effect.moveExempt.join(', ')} exempt)` : '';
    lines.push(`• Stops movement on entry${exempt}`);
  }
  if (effect.maxMoveDistIfEntering !== undefined) {
    // Value = max hexes a unit may have traveled when entering this hex
    if (effect.maxMoveDistIfEntering <= 1) {
      lines.push(`• Can only enter from an adjacent hex`);
    } else {
      lines.push(`• A unit that moves onto this hex may only move ${effect.maxMoveDistIfEntering} hexes total`);
    }
  }
  if (effect.impassableForRetreat) {
    lines.push(`• Cannot retreat into this hex`);
  }
  if (effect.pinnedTypes && effect.pinnedTypes.length > 0) {
    lines.push(`• ${effect.pinnedTypes.join(', ')} cannot move from here`);
  }
  if (effect.attritionOnEntry) {
    lines.push(`• Entering rolls ${effect.attritionOnEntry.dice} dice — lose 1 figure per Star`);
  }
  if (effect.leavingMoveLimit !== undefined) {
    lines.push(`• A unit leaving this hex may only move ${effect.leavingMoveLimit} hex${effect.leavingMoveLimit > 1 ? 'es' : ''} that turn`);
  }

  // Battle
  if (effect.noBattleFromHexTypes && effect.noBattleFromHexTypes.length > 0) {
    lines.push(`✗ Cannot battle from this hex (${effect.noBattleFromHexTypes.join(', ')})`);
  }
  if (effect.blocksBattleAfterLeavingTypes && effect.blocksBattleAfterLeavingTypes.length > 0) {
    lines.push(`✗ Cannot battle the turn it leaves this hex (${effect.blocksBattleAfterLeavingTypes.join(', ')})`);
  }
  if (effect.blocksBattleAfterMove) {
    const scope = effect.blocksBattleAfterMoveTypes && effect.blocksBattleAfterMoveTypes.length > 0
      ? ` (${effect.blocksBattleAfterMoveTypes.join(', ')} only)` : '';
    const ok = effect.battleAfterMoveOk;
    if (ok && ok.length > 0) {
      lines.push(`• Cannot battle after moving in${scope} (${ok.join(', ')} exempt)`);
    } else {
      lines.push(`• Cannot battle after moving in${scope}`);
    }
  }

  // Defense bonuses
  const { infantry: infDef, armor: armDef, artillery: artDef } = effect.defenseBonus;
  if (infDef || armDef || artDef) {
    const b: string[] = [];
    if (infDef) b.push(`+${infDef} vs infantry`);
    if (armDef) b.push(`+${armDef} vs armor`);
    if (artDef) b.push(`+${artDef} vs artillery`);
    lines.push(`⬆ Defense: ${b.join(', ')}`);
  }

  // Attack penalties
  const { infantry: infAtk, armor: armAtk, artillery: artAtk } = effect.attackPenalty;
  if (infAtk || armAtk || artAtk) {
    const p: string[] = [];
    if (infAtk) p.push(`−${infAtk} infantry`);
    if (armAtk) p.push(`−${armAtk} armor`);
    if (artAtk) p.push(`−${artAtk} artillery`);
    lines.push(`⬇ Attack dice: ${p.join(', ')}`);
  }

  // Flags & LoS
  if (effect.ignoreFlags) {
    const scope = effect.ignoreFlagsTypes && effect.ignoreFlagsTypes.length > 0
      ? ` (${effect.ignoreFlagsTypes.join(', ')} only)` : '';
    lines.push(`• Ignores ${effect.ignoreFlags} retreat flag${effect.ignoreFlags > 1 ? 's' : ''}${scope}`);
  }
  if (effect.blocksLoS) {
    lines.push(`• Blocks line of sight`);
  }
  if (overlay && permanent) {
    lines.push(`• Permanent — never removed when units leave`);
  }

  if (lines.length === 2) {
    lines.push('• No special rules');
  }

  return lines.join('\n');
}
